import { memo, type ReactNode } from 'react';

import { type Board, type Board as BoardInterface } from '@air/api/types';

import { BoardGalleryCard, type BoardGalleryCardProps } from '~/components/Gallery/BoardGalleryCard/BoardGalleryCard';
import { DraggingOverlay } from '~/components/Gallery/DraggingOverlay';
import { DNDParentBoard } from '~/components/Shared/Drag/DNDParentBoard';
import DragType from '~/components/Shared/Drag/dragTypes';
import { type DragDroppableBoardShellProps, DraggableBoardCard } from '~/components/Shared/DraggableBoardContainer';
import { DropHighlight } from '~/components/Shared/DropHighlight';

type ReactDNDBoardCardContainerProps = {
  board: BoardInterface;
  canDropAssets: boolean;
  children: ReactNode;
};

const ReactDNDBoardCardContainer = memo(({ board, canDropAssets, children }: ReactDNDBoardCardContainerProps) =>
  canDropAssets ? (
    <DNDParentBoard dropLocation="board" board={board}>
      <DropHighlight borderRadius={18} />
      {children}
    </DNDParentBoard>
  ) : (
    // requires fragment wrapper because TS needs JSX.Element (React 18.0.x)
    <>{children}</>
  ),
);

ReactDNDBoardCardContainer.displayName = 'ReactDNDBoardCardContainer';

export type DNDBoardGalleryCardProps = {
  canDropAssets?: boolean;
  enableDrag?: boolean;
  index?: number;
} & BoardGalleryCardProps<Board> &
  Pick<DragDroppableBoardShellProps, 'onDragStart'>;

export const DNDBoardGalleryCard = memo(
  ({ board, canDropAssets = false, enableDrag = true, index, onDragStart, ...rest }: DNDBoardGalleryCardProps) => {
    return (
      <DraggableBoardCard board={board} enabled={enableDrag} index={index} onDragStart={onDragStart}>
        <ReactDNDBoardCardContainer canDropAssets={canDropAssets} board={board}>
          <BoardGalleryCard board={board} {...rest} />
          <DraggingOverlay itemId={board.id} type={DragType.boardCard} />
        </ReactDNDBoardCardContainer>
      </DraggableBoardCard>
    );
  },
);

DNDBoardGalleryCard.displayName = 'DNDBoardGalleryCard';
